import React from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  List,
  ListItem,
  Radio,
  FormControl
} from '@mui/material';
import { radioClasses } from '@mui/joy/Radio';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { AttributeIconMap } from '@gamma/shared/icons';
import { config } from '../config/env';

/**
 * AttributeSelector - Reusable component for selecting attribute values using icons
 *
 * @param {string} attributeType - Display name of attribute (e.g., 'Gender', 'AgeGroup')
 * @param {Array<string>} values - Array of possible values for this attribute
 * @param {string} selectedValue - Currently selected value
 * @param {function} onChange - Callback when selection changes: (attributeType, newValue) => void
 * @param {number} iconSize - Size of icons in pixels (default: 40)
 * @param {boolean} disabled - Whether selector is disabled
 * @param {boolean} showLabel - Whether to show attribute type label (default: true)
 * @param {boolean} allowDeselect - Whether clicking selected value deselects it (default: false)
 */
export default function AttributeSelector({
  attributeType,
  values,
  selectedValue,
  onChange,
  iconSize = 40,
  disabled = false,
  showLabel = false,
  allowDeselect = false
}) {
  // Skip if only one value (not a real choice)
  if (values.length === 1) {
    return null;
  }

  const handleClick = (value) => {
    if (disabled) return;

    if (selectedValue === value && allowDeselect) {
      // Deselect if clicking already selected value
      onChange(attributeType, null);
    } else {
      // Select new value
      onChange(attributeType, value);
    }
  };

  const getIconPath = (value) => {
    const iconMap = AttributeIconMap[attributeType];
    if (!iconMap) {
      console.warn(`[AttributeSelector] No icon map for "${attributeType}"`);
      return undefined;
    }

    const relativePath = iconMap[value];
    if (!relativePath) {
      console.warn(`[AttributeSelector] No icon for "${attributeType}.${value}"`);
      return undefined;
    }

    return config.publicUrl + relativePath;
  };

  return (
    <Box sx={{ mb: 1 }}>
      {showLabel && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            fontSize: '0.8rem',
            color: 'text.secondary'
          }}
        >
          {attributeType}
        </Typography>
      )}

      <FormControl disabled={disabled} fullWidth>
        <RadioGroup
          value={selectedValue || ''}
          sx={{
            flexDirection: 'row',
            gap: 1,
            [`& .${radioClasses.checked}`]: {
              [`& .${radioClasses.action}`]: {
                inset: -1,
                border: '3px solid',
                borderColor: 'primary.500',
              },
            },
            [`& .${radioClasses.radio}`]: {
              display: 'contents',
              '& > svg': {
                zIndex: 2,
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                bgcolor: 'background.body',
                borderRadius: '50%',
              },
            },
          }}
        >
          <List
            orientation="horizontal"
            sx={{
              minWidth: 0,
              '--List-gap': '0.75rem',
              '--ListItem-paddingY': '0.5rem',
              '--ListItem-paddingX': '0.25rem',
              '--ListItem-radius': '8px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              p: 0
            }}
          >
            {values.map((value) => {
              const iconPath = getIconPath(value);
              const isSelected = selectedValue === value;

              return (
                <ListItem
                  key={value}
                  variant="outlined"
                  onClick={() => handleClick(value)}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                    boxShadow: 'sm',
                    bgcolor: 'background.paper',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    border: isSelected ? '3px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    '&:hover': {
                      borderColor: disabled ? 'divider' : 'primary.light',
                      bgcolor: disabled ? 'background.paper' : 'action.hover'
                    }
                  }}
                >
                  {iconPath && (
                    <img
                      src={iconPath}
                      alt={value}
                      style={{
                        width: iconSize - 10,
                        height: iconSize - 10,
                        objectFit: 'contain'
                      }}
                    />
                  )}

                  {isSelected && (
                    <CheckCircleRoundedIcon
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        fontSize: 20,
                        color: 'primary.main',
                        bgcolor: 'background.paper',
                        borderRadius: '50%'
                      }}
                    />
                  )}

                  <Radio
                    value={value}
                    checked={isSelected}
                    disabled={disabled}
                    sx={{ display: 'none' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </RadioGroup>
      </FormControl>
    </Box>
  );
}
